import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { formatDateTime } from "../ui";
import { Spinner, StatusBadge, fmtBRL } from "./despesas/shared";
import { useExpenseDetail } from "./despesas/useExpenseDetail";
import { ExpenseHeaderForm } from "./despesas/ExpenseHeaderForm";
import { ExpenseItems } from "./despesas/ExpenseItems";
import { ApprovalsTimeline, PaymentProof } from "./despesas/ExpenseHistory";
import { ExpenseActions } from "./despesas/ExpenseActions";
import { DecisionModal } from "./despesas/DecisionModal";

export { ExpensesList } from "./despesas/ExpensesList";
export { AdminExpensesList } from "./despesas/AdminExpensesList";

const DetailTitle = ({ isNew, doc }) => (
  <div className="flex items-start justify-between gap-4 flex-wrap">
    <div>
      <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500">
        Financeiro · Restituição de Despesas
      </div>
      <h1 className="mt-1 font-serif-display text-3xl">{isNew ? "Novo relatório" : doc?.title}</h1>
      {doc && (
        <div className="mt-2 flex items-center gap-3 flex-wrap">
          <StatusBadge status={doc.status} />
          <span className="text-xs text-slate-500">
            Por <strong>{doc.author_name}</strong>
            {doc.created_at && ` · ${formatDateTime(doc.created_at)}`}
          </span>
        </div>
      )}
    </div>
    {doc && (
      <div className="text-right">
        <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500">Total</div>
        <div className="mt-1 font-serif-display text-3xl" data-testid="expense-total">
          {fmtBRL(doc.total)}
        </div>
      </div>
    )}
  </div>
);

// CREATE / EDIT / REVIEW — /portal/despesas/novo | /portal/despesas/:id (and /admin/...)
export function ExpenseDetail() {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const backTo = window.location.pathname.startsWith("/admin/") ? "/admin/despesas" : "/portal/despesas";
  const x = useExpenseDetail(routeId, backTo);
  const [decision, setDecision] = useState(null);

  if (x.loading) return <Spinner />;

  const { doc } = x;
  const isDraft = doc?.status === "rascunho";

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-8" data-testid="expense-detail-page">
      <button
        onClick={() => navigate(backTo)}
        data-testid="expense-back-btn"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-slate-500 hover:text-slate-800 mb-4"
      >
        <ArrowLeft size={14} /> Voltar
      </button>
      <DetailTitle isNew={x.isNew} doc={doc} />
      <ExpenseHeaderForm
        header={x.header}
        setHeader={x.setHeader}
        editable={x.isNew || isDraft}
        isNew={x.isNew}
        saving={x.saving}
        onSave={x.saveHeader}
        payers={x.payers}
        payerName={doc?.payer_name}
      />
      {doc && (
        <ExpenseItems doc={doc} reportId={x.id} isDraft={isDraft} onRemove={x.removeItem}
          onDownload={x.downloadReceipt} onAdded={x.setDoc} />
      )}
      {doc?.approvals?.length > 0 && <ApprovalsTimeline approvals={doc.approvals} />}
      {doc?.payment_proof && <PaymentProof doc={doc} />}
      {doc && (
        <ExpenseActions perms={doc.permissions || {}} hasItems={(doc.items || []).length > 0}
          submitting={x.submitting} onCancel={x.cancel} onSubmit={x.submit} onDecide={setDecision} />
      )}
      {decision && (
        <DecisionModal
          action={decision}
          onClose={() => setDecision(null)}
          onConfirm={async (action, text) => {
            await x.decide(action, text);
            setDecision(null);
          }}
        />
      )}
    </div>
  );
}
